/**
 * Das Feld eines Stammdaten-Formulars — einmal für alle Fachbereiche.
 *
 * **Warum das vorher viermal dastand.** `features/*` dürfen einander nicht
 * importieren (eslint-boundaries), und daraus wurde geschlossen, es müsse
 * kopiert werden. Die Regel sagt aber etwas anderes, wörtlich in
 * `eslint.config.js`: *„Gemeinsames gehört nach `components/` oder `lib/`"*.
 * Genau hier ist es.
 *
 * Gefunden hat es `check_duplication()` in `make check` (ADR 0013): Die vier
 * `fields.tsx` der Fachbereiche stimmten zu 67–77 % überein, `FIELD_CLASS` war
 * byte-identisch.
 */

import type { InputHTMLAttributes, Ref } from 'react'

export const FIELD_CLASS =
  'border-border bg-surface text-text rounded-card border px-3 text-body ' +
  'focus-visible:border-accent transition-colors duration-(--duration-fast) ease-(--ease-out) ' +
  'disabled:opacity-60'

/** Das `field` aus einer API-Fehlerantwort — wörtlich der Name der
 * Eigenschaft, nicht der `data-testid`-Name des Feldes. */
export interface FieldError {
  readonly field: string
  readonly message: string
}

interface FormFieldProps {
  readonly label: string
  /** `data-testid` des Eingabefelds selbst, z. B. `property-create-street`. */
  readonly testId: string
  /** Name der Eigenschaft in der API-Antwort, z. B. `street` oder
   * `acquisition_cost_cents` — bestimmt zusammen mit `errorPrefix` den
   * `data-testid` der Fehlermeldung. */
  readonly apiField: string
  /** `<art>-create` oder `<art>-edit`, z. B. `building-create`. */
  readonly errorPrefix: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error: FieldError | undefined
  readonly type?: InputHTMLAttributes<HTMLInputElement>['type']
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode']
  /** Z. B. `characters` für Zählernummern (Spec 46a). */
  readonly autoCapitalize?: InputHTMLAttributes<HTMLInputElement>['autoCapitalize']
  readonly placeholder?: string
  /** Obergrenze eines `type="date"`-Feldes (Spec 46b: Zukunft gesperrt). */
  readonly max?: string
  readonly required?: boolean
  readonly disabled?: boolean
  readonly ref?: Ref<HTMLInputElement>
}

export function FormField({
  label,
  testId,
  apiField,
  errorPrefix,
  value,
  onChange,
  error,
  type = 'text',
  inputMode,
  autoCapitalize,
  placeholder,
  max,
  required = false,
  disabled = false,
  ref,
}: FormFieldProps) {
  const hasError = error?.field === apiField
  const errorTestId = `${errorPrefix}-error-${apiField}`

  return (
    <label className="text-label text-muted flex flex-col gap-1" htmlFor={testId}>
      {label}
      <input
        id={testId}
        ref={ref}
        data-testid={testId}
        type={type}
        inputMode={inputMode}
        autoCapitalize={autoCapitalize}
        placeholder={placeholder}
        max={max}
        required={required}
        disabled={disabled}
        value={value}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorTestId : undefined}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        className={`text-text ${FIELD_CLASS}`}
      />
      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </label>
  )
}

interface FormTextareaFieldProps {
  readonly label: string
  readonly testId: string
  readonly apiField: string
  readonly errorPrefix: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error: FieldError | undefined
  readonly disabled?: boolean
}

/** Das mehrzeilige Gegenstück zu `FormField` — bisher nur „Notizen" (Gebäude,
 * Spec 02a, „Reiter Stammdaten"). Eigene Komponente statt eines
 * `multiline`-Zweigs in `FormField`: Ein `<textarea>` hat einen anderen
 * `ref`-Typ als `<input>`, und `FormField` wird über ein Sheet-Autofokus-Ref
 * angesprochen, das „Notizen" nie braucht. */
export function FormTextareaField({
  label,
  testId,
  apiField,
  errorPrefix,
  value,
  onChange,
  error,
  disabled = false,
}: FormTextareaFieldProps) {
  const hasError = error?.field === apiField
  const errorTestId = `${errorPrefix}-error-${apiField}`

  return (
    <label className="text-label text-muted flex flex-col gap-1" htmlFor={testId}>
      {label}
      <textarea
        id={testId}
        data-testid={testId}
        rows={3}
        disabled={disabled}
        value={value}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorTestId : undefined}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        className={`text-text ${FIELD_CLASS} py-2`}
      />
      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </label>
  )
}
